// /src/hooks/useScenariosData.js
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";

import {
	retrieveScenarios,
	retrieveOneScenario,
	retrieveOneScenarioByUuid,
	setSelectedScenario,
} from "../slices/scenarioSlice";
import API from "../http-common"; // shared axios instance

import { debugLog, errorLog } from "../utils/debugUtils";
import { prepareScenarioObject } from "../utils/scenarioUtils";
import { isObject } from "../utils/dataUtils";

// Custom hook centralizes Scenario API logic
export const useScenariosData = () => {
	const dispatch = useDispatch();

	// Fetch all scenarios (for ScenarioList)
	const fetchScenarios = useCallback(() => {
		debugLog(3, "[useScenariosData][ACTION] fetchScenarios", {});
		debugLog(2, "[useScenariosData][FLOW] retrieveScenarios started", {});

		trackPromise(
			dispatch(retrieveScenarios())
				.unwrap()
				.then((response) => {
					// Scenario list now in Redux: state.scenarios.list
					debugLog(3, "[useScenariosData][STATE] scenarios retrieved", {
						scenarios: response,
						count: Array.isArray(response) ? response.length : undefined,
					});
				})
				.catch((err) => {
					errorLog(2, "[useScenariosData][FLOW] retrieveScenarios failed", { error: err });
					console.error("fetchScenarios failed", err);
				}),
		);
	}, [dispatch]);

	// Fetch single scenario by id or uuid (for deep linking or details)
	const fetchScenarioByIdOrUuid = useCallback(
		(idOrUuid) => {
			debugLog(3, "[useScenariosData][ACTION] fetchScenarioByIdOrUuid", { idOrUuid: idOrUuid });
			debugLog(2, "[useScenariosData][FLOW] retrieveOneScenario started", { idOrUuid: idOrUuid });

			const action = isNaN(idOrUuid)
				? retrieveOneScenarioByUuid(idOrUuid)
				: retrieveOneScenario(idOrUuid);

			trackPromise(
				dispatch(action)
					.unwrap()
					.then((response) => {
						// Prepare and set the scenario in Redux (selectedScenario)
						const preparedScenario = prepareScenarioObject(response);
						debugLog(3, "[useScenariosData][STATE] scenario retrieved", {
							preparedScenario: preparedScenario,
						});

						dispatch(setSelectedScenario(preparedScenario));
					})
					.catch((err) => {
						errorLog(2, "[useScenariosData][FLOW] retrieveOneScenario failed", {
							idOrUuid: idOrUuid,
							error: err,
						});
						console.error("fetchScenarioByIdOrUuid failed", err);
					}),
			);
		},
		[dispatch],
	);

	/**
	 * Silent fetch for export: returns a prepared scenario without touching Redux.
	 * Accepts id or uuid (same as fetchScenarioByIdOrUuid), with opt-in photos.
	 */
	const fetchScenarioForExport = useCallback(async (idOrUuid, { includePhotos = true } = {}) => {
		debugLog(3, "[useScenariosData][ACTION] fetchScenarioForExport", {
			idOrUuid,
			includePhotos,
		});
		debugLog(2, "[useScenariosData][FLOW] scenario export API started", { idOrUuid, includePhotos });
		const byUuid = isNaN(idOrUuid);
		const url = byUuid ? `/scenarios/uuid/${idOrUuid}` : `/scenarios/${idOrUuid}`;
		// Use trackPromise so your global loader can reflect work-in-progress
		const { data } = await trackPromise(API.get(url, { params: { includePhotos } }));
		const prepared = prepareScenarioObject(data);
		debugLog(4, "[useScenariosData][PROCESS] fetchScenarioForExport prepared", {
			items: prepared?.baseline?.baseline_items?.length ?? 0,
			includePhotos,
		});
		return prepared;
	}, []);

	// UI handler for list selection; normalizes scenario and updates Redux
	const handleSelectScenario = useCallback(
		(scenario) => {
			debugLog(3, "[useScenariosData][ACTION] handleSelectScenario", { scenario: scenario });

			const id = isObject(scenario) ? scenario.id : scenario;
			fetchScenarioByIdOrUuid(id);
		},
		[fetchScenarioByIdOrUuid],
	);

	return {
		fetchScenarios,
		fetchScenarioByIdOrUuid,
		fetchScenarioForExport,
		handleSelectScenario,
	};
};
